import {
  Box,
  Heading,
  Button,
  Badge,
  Code,
  Text,
  HStack,
  VStack,
  Tooltip,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
} from '@chakra-ui/react'
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FiEye } from 'react-icons/fi'
import {
  useApproveMerchantChain,
  useMerchantChainReviews,
  useMerchants,
  useRejectMerchantChain,
} from '@/hooks/useAdminData'
import { useQueryClient } from '@tanstack/react-query'
import { MerchantDetailModal } from '@/components/merchants/MerchantDetailModal'
import { MerchantChainReviewDialog } from '@/components/merchants/MerchantChainReviewDialog'
import { DataTable } from '@/components/common/DataTable'
import { ColumnConfig, FilterConfig, PaginationConfig, ActionButton } from '@/components/common/DataTable/types'
import { StatusBadge } from '@/components/common/StatusBadge'
import { useCopyableCodeStyles } from '@/components/common/useCopyableCodeStyles'
import { formatDateTime } from '@/utils/formatUtils'
import { useToast } from '@/hooks/useToast'
import { ADMIN_MERCHANT_FILTER_STORAGE_KEY, withNetworkStorageKey } from '@/utils/storageKeys'
import {
  readFunctionalStorage,
  removeFunctionalStorage,
  writeFunctionalStorage,
} from '@/utils/privacyPreferences'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { InlineSkeleton } from '@/components/common/LoadingSkeleton'
import { IdLookupPanel } from '@/components/common/IdLookupPanel'
import { triggerBrowserDownload } from '@/services/admin/baseService'
import { merchantService } from '@/services/admin/merchantService'
import type { MerchantChainReviewItem } from '@/types'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

export function MerchantsPage() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const filterStorageKey = useMemo(
    () => withNetworkStorageKey(ADMIN_MERCHANT_FILTER_STORAGE_KEY, currentEnv),
    [currentEnv]
  )
  const [page, setPage] = useState(1)
  
  const loadStoredFilters = () => {
    if (typeof window === 'undefined') {
      return { persist: false, filters: {} as Record<string, any> }
    }

    try {
      const raw = readFunctionalStorage(filterStorageKey)
      if (!raw) {
        return { persist: false, filters: {} as Record<string, any> }
      }

      const parsed = JSON.parse(raw)
      if (parsed?.persist) {
        return {
          persist: true,
          filters: parsed.filters ?? {},
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[MerchantsPage] Failed to load stored filters', error)
      }
      removeFunctionalStorage(filterStorageKey)
    }

    return { persist: false, filters: {} as Record<string, any> }
  }

  const storedConfig = useMemo(loadStoredFilters, [filterStorageKey])

  const [persistFilters, setPersistFilters] = useState<boolean>(storedConfig.persist)
  const [filters, setFilters] = useState<Record<string, any>>(storedConfig.filters)
  const [reviewPage, setReviewPage] = useState(1)
  const [reviewFilters, setReviewFilters] = useState<Record<string, any>>({
    approvalStatus: 'pending_review',
    chainCompatibility: '',
    merchantStatus: '',
    keyword: '',
  })
  useEffect(() => {
    setPersistFilters(storedConfig.persist)
    setFilters(storedConfig.filters)
    setPage(1)
  }, [storedConfig])
  const [selectedMerchantId, setSelectedMerchantId] = useState<string | null>(null)
  const [chainReviewAction, setChainReviewAction] = useState<{
    action: 'approve' | 'reject'
    merchantId: string
    merchantName: string
    compatibility: 'evm' | 'tron' | 'solana'
    reviewRevisionId: string
  } | null>(null)
  const limit = 20
  const queryClient = useQueryClient()
  const toast = useToast()

  const { data, isLoading, error, isFetching, dataUpdatedAt } = useMerchants({
    page,
    limit,
    search: filters.search || '',
    status: filters.status || '',
  })
  const reviewPageSize = 10
  const { data: pendingReviews, isLoading: isReviewLoading, error: reviewError } = useMerchantChainReviews({
    page: reviewPage,
    pageSize: reviewPageSize,
    approvalStatus: reviewFilters.approvalStatus || undefined,
    chainCompatibility: reviewFilters.chainCompatibility || undefined,
    merchantStatus: reviewFilters.merchantStatus || undefined,
    keyword: reviewFilters.keyword || undefined,
  })
  const { data: pendingReviewSummary } = useMerchantChainReviews({
    page: 1,
    pageSize: 1,
    approvalStatus: 'pending_review',
  })
  const { data: pendingEvmSummary } = useMerchantChainReviews({
    page: 1,
    pageSize: 1,
    approvalStatus: 'pending_review',
    chainCompatibility: 'evm',
  })
  const { data: pendingTronSummary } = useMerchantChainReviews({
    page: 1,
    pageSize: 1,
    approvalStatus: 'pending_review',
    chainCompatibility: 'tron',
  })
  const { data: pendingSolanaSummary } = useMerchantChainReviews({
    page: 1,
    pageSize: 1,
    approvalStatus: 'pending_review',
    chainCompatibility: 'solana',
  })
  const approveChain = useApproveMerchantChain()
  const rejectChain = useRejectMerchantChain()
  
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const subtitleColor = useColorModeValue('gray.600', 'gray.300')
  const labelColor = useColorModeValue('gray.600', 'gray.400')
  const copyableCodeStyles = useCopyableCodeStyles()

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['merchants'] })
    queryClient.invalidateQueries({ queryKey: ['merchant-chain-reviews'] })
  }

  // Format last updated time
  const getLastUpdatedText = () => {
    if (!dataUpdatedAt) return ''
    const now = new Date()
    const updated = new Date(dataUpdatedAt)
    const diffInSeconds = Math.floor((now.getTime() - updated.getTime()) / 1000)
    
    if (diffInSeconds < 60) return `${t('common.updated')} ${diffInSeconds}s ago`
    if (diffInSeconds < 3600) return `${t('common.updated')} ${Math.floor(diffInSeconds / 60)}m ago`
    return `${t('common.updated')} ${Math.floor(diffInSeconds / 3600)}h ago`
  }

  const getStatusText = (statusCode: string) => {
    switch (statusCode) {
      case 'mst_active':
        return t('merchants.status.active')
      case 'mst_partial_active':
        return t('merchants.status.partialActive')
      case 'mst_pending_config':
        return t('merchants.status.pendingConfig')
      case 'mst_suspended':
        return t('merchants.status.suspended')
      case 'mst_terminated':
        return t('merchants.status.terminated')
      default:
        return statusCode
    }
  }

  const copyToClipboard = async (value: string, label: string) => {
    if (!value) return

    try {
      await navigator.clipboard.writeText(value)
      toast.success({
        title: t('common.copySuccess', 'Copied'),
        description: `${label} copied`,
      })
    } catch {
      toast.error({
        title: t('common.copyFailed', 'Copy failed'),
        description: `Unable to copy ${label.toLowerCase()}`,
      })
    }
  }

  const handleChainReviewConfirm = async (reason: string) => {
    if (!chainReviewAction) return

    try {
      if (chainReviewAction.action === 'approve') {
        await approveChain.mutateAsync({
          merchantId: chainReviewAction.merchantId,
          compatibility: chainReviewAction.compatibility,
          reviewRevisionId: chainReviewAction.reviewRevisionId,
        })
      } else {
        await rejectChain.mutateAsync({
          merchantId: chainReviewAction.merchantId,
          compatibility: chainReviewAction.compatibility,
          reason,
          reviewRevisionId: chainReviewAction.reviewRevisionId,
        })
      }
      setChainReviewAction(null)
      toast.success({
        title: t('merchants.reviewList.actions.successTitle'),
        description: t('merchants.reviewList.actions.successDescription', {
          chain: getChainLabel(chainReviewAction.compatibility),
        }),
      })
    } catch (error) {
      const responseData = (error as any)?.response?.data
      const errorMessage =
        responseData?.error?.message ??
        responseData?.message ??
        (error instanceof Error ? error.message : t('common.operationFailed'))
      toast.error({
        title: t('merchantDetail.chainReviewFailedTitle'),
        description: errorMessage,
      })
    }
  }

  const getChainLabel = (compatibility?: string | null) => {
    switch (compatibility) {
      case 'evm':
        return t('merchants.reviewList.chainLabels.evm')
      case 'tron':
        return t('merchants.reviewList.chainLabels.tron')
      case 'solana':
        return t('merchants.reviewList.chainLabels.solana')
      default:
        return t('common.unknown')
    }
  }

  const getApprovalStatusColor = (approvalStatus?: string | null) => {
    switch (approvalStatus) {
      case 'approved':
        return 'green'
      case 'pending_review':
        return 'blue'
      case 'rejected':
        return 'red'
      case 'not_submitted':
      default:
        return 'gray'
    }
  }

  const getApprovalStatusText = (approvalStatus?: string | null) => {
    switch (approvalStatus) {
      case 'approved':
        return t('merchants.reviewList.status.approved')
      case 'pending_review':
        return t('merchants.reviewList.status.pendingReview')
      case 'rejected':
        return t('merchants.reviewList.status.rejected')
      case 'not_submitted':
      default:
        return t('merchants.reviewList.status.notSubmitted')
    }
  }

  // Define column configuration for DataTable
  const columns: ColumnConfig<any>[] = [
    {
      key: 'merchantId',
      title: t('merchants.columns.businessId'),
      width: '15%',
      render: (value) => (
        <Tooltip label={value ? t('common.copy') : ''}>
          <Box minH="24px" display="flex" alignItems="center" justifyContent="flex-start">
            <Code
              as="div"
              bg="transparent"
              fontSize="xs"
              color={textColor}
              fontFamily="mono"
              lineHeight="1.2"
              wordBreak="break-all"
              cursor={value ? 'pointer' : 'default'}
              _hover={value ? copyableCodeStyles.hover : undefined}
              _active={value ? copyableCodeStyles.active : undefined}
              onClick={() => value && copyToClipboard(value, 'Business ID')}
            >
              {value || t('common.pending')}
            </Code>
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'username',
      title: t('merchants.columns.username'),
      width: '12%',
      render: (value, row) => {
        const displayValue = value || row.user_id || t('common.pending')
        const copyValue = value || row.user_id
        const email = row.email
        return (
          <Box minH="40px">
            <Tooltip label={copyValue ? t('common.copy') : ''}>
              <Box display="flex" alignItems="center" justifyContent="flex-start">
                <Code
                  as="div"
                  bg="transparent"
                  fontSize="sm"
                  color={labelColor}
                  maxW="100px"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  cursor={copyValue ? 'pointer' : 'default'}
                  _hover={copyValue ? copyableCodeStyles.hover : undefined}
                  _active={copyValue ? copyableCodeStyles.active : undefined}
                  onClick={() => copyValue && copyToClipboard(copyValue, 'Username')}
                >
                  {displayValue}
                </Code>
              </Box>
            </Tooltip>
            {email && (
              <Tooltip label={t('common.copy')}>
                <Text
                  mt={1}
                  fontSize="xs"
                  color={labelColor}
                  noOfLines={1}
                  title={email}
                  cursor="pointer"
                  onClick={() => copyToClipboard(email, 'Email')}
                >
                  {email}
                </Text>
              </Tooltip>
            )}
          </Box>
        )
      },
    },
    {
      key: 'name',
      title: t('merchants.columns.name'),
      width: '15%',
      render: (value) => (
        <Text
          fontWeight="medium"
          fontSize="sm"
          color={textColor}
          noOfLines={1}
          title={value}
          maxW="120px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {value}
        </Text>
      ),
    },
    {
      key: 'description',
      title: t('merchants.columns.description'),
      width: '20%',
      render: (value) => (
        <Text 
          fontSize="sm" 
          color={labelColor} 
          title={value || 'None'}
          maxW="200px"
          overflow="hidden"
          textOverflow="ellipsis"
          display="-webkit-box"
          sx={{
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical"
          }}
        >
          {value || 'None'}
        </Text>
      ),
    },
    {
      key: 'status',
      title: t('merchants.columns.status'),
      width: '10%',
      render: (value) => (
        <StatusBadge statusType="merchant" status={value} label={getStatusText(value)} />
      ),
    },
    {
      key: 'created_at',
      title: t('merchants.columns.created'),
      width: '10%',
      render: (value) => (
        <Text fontSize="xs" color={labelColor}>
          {value ? formatDateTime(value) : t('common.na')}
        </Text>
      ),
    },
  ]

  // Define filter configuration for DataTable
  const filterConfigs: FilterConfig[] = [
    {
      key: 'search',
      type: 'text',
      label: t('merchants.searchPlaceholder'),
      placeholder: t('merchants.searchPlaceholder'),
      width: '300px',
    },
    {
      key: 'status',
      type: 'select',
      label: t('merchants.filterByStatus'),
      placeholder: t('merchants.filterByStatus'),
      width: '200px',
      options: [
        { value: 'mst_active', label: t('merchants.status.active') },
        { value: 'mst_partial_active', label: t('merchants.status.partialActive') },
        { value: 'mst_pending_config', label: t('merchants.status.pendingConfig') },
        { value: 'mst_suspended', label: t('merchants.status.suspended') },
        { value: 'mst_terminated', label: t('merchants.status.terminated') },
      ],
    },
  ]

  // Define pagination configuration
  const paginationConfig: PaginationConfig = {
    page,
    pageSize: limit,
    total: data?.pagination?.total || 0,
    pageSizeOptions: [10, 20, 50],
  }

  const reviewPaginationConfig: PaginationConfig = {
    page: reviewPage,
    pageSize: reviewPageSize,
    total: pendingReviews?.pagination?.total || 0,
    pageSizeOptions: [10],
  }

  const reviewColumns: ColumnConfig<MerchantChainReviewItem>[] = [
    {
      key: 'merchant',
      title: t('merchants.reviewList.columns.merchant'),
      width: '24%',
      render: (_value, row) => (
        <Box>
          <Text fontWeight="medium" fontSize="sm" color={textColor} noOfLines={1}>
            {row.merchant.name || t('common.unknown')}
          </Text>
          <Text fontSize="xs" color={labelColor} fontFamily="mono">
            {row.merchant.merchantId || row.merchant.id}
          </Text>
        </Box>
      ),
    },
    {
      key: 'chainAccess.compatibility',
      title: t('merchants.reviewList.columns.chain'),
      width: '14%',
      render: (_value, row) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm" color={textColor}>
            {getChainLabel(row.chainAccess.compatibility)}
          </Text>
          {!row.chainAccess.planAllowed && (
            <Badge colorScheme="orange" fontSize="xs">
              {t('merchantChainReview.planRestricted', 'Plan restricted')}
            </Badge>
          )}
        </VStack>
      ),
    },
    {
      key: 'chainAccess.walletAddress',
      title: t('merchants.reviewList.columns.wallet'),
      width: '26%',
      render: (_value, row) => (
        <Text fontSize="sm" color={labelColor} fontFamily="mono" noOfLines={1}>
          {row.chainAccess.walletAddress || t('merchantDetail.walletAddressNotVerified')}
        </Text>
      ),
    },
    {
      key: 'chainAccess.approvalStatus',
      title: t('merchants.reviewList.columns.status'),
      width: '14%',
      render: (_value, row) => (
        <Badge colorScheme={getApprovalStatusColor(row.chainAccess.approvalStatus)}>
          {getApprovalStatusText(row.chainAccess.approvalStatus)}
        </Badge>
      ),
    },
    {
      key: 'chainAccess.reviewSubmittedAt',
      title: t('merchants.reviewList.columns.submittedAt'),
      width: '17%',
      render: (_value, row) => (
        <Text fontSize="xs" color={labelColor}>
          {row.chainAccess.reviewSubmittedAt
            ? formatDateTime(row.chainAccess.reviewSubmittedAt)
            : t('common.na')}
        </Text>
      ),
    },
  ]

  const reviewFilterConfigs: FilterConfig[] = [
    {
      key: 'keyword',
      type: 'text',
      label: t('merchants.reviewList.filters.keyword'),
      placeholder: t('merchants.reviewList.filters.keywordPlaceholder'),
      width: '280px',
    },
    {
      key: 'chainCompatibility',
      type: 'select',
      label: t('merchants.reviewList.filters.chain'),
      placeholder: t('merchants.reviewList.filters.chainPlaceholder'),
      width: '160px',
      options: [
        { value: 'evm', label: t('merchants.reviewList.chainLabels.evm') },
        { value: 'tron', label: t('merchants.reviewList.chainLabels.tron') },
        { value: 'solana', label: t('merchants.reviewList.chainLabels.solana') },
      ],
    },
    {
      key: 'merchantStatus',
      type: 'select',
      label: t('merchants.reviewList.filters.merchantStatus'),
      placeholder: t('merchants.filterByStatus'),
      width: '180px',
      options: [
        { value: 'mst_active', label: t('merchants.status.active') },
        { value: 'mst_partial_active', label: t('merchants.status.partialActive') },
        { value: 'mst_pending_config', label: t('merchants.status.pendingConfig') },
        { value: 'mst_suspended', label: t('merchants.status.suspended') },
        { value: 'mst_terminated', label: t('merchants.status.terminated') },
      ],
    },
    {
      key: 'approvalStatus',
      type: 'select',
      label: t('merchants.reviewList.filters.reviewStatus'),
      placeholder: t('merchants.reviewList.filters.reviewStatusPlaceholder'),
      width: '180px',
      options: [
        { value: 'pending_review', label: t('merchants.reviewList.status.pendingReview') },
        { value: 'approved', label: t('merchants.reviewList.status.approved') },
        { value: 'rejected', label: t('merchants.reviewList.status.rejected') },
        { value: 'not_submitted', label: t('merchants.reviewList.status.notSubmitted') },
      ],
    },
  ]

  // Get actions for each row
  const getRowActions = (merchant: any): ActionButton[] => {
    return [
      {
        label: '',
        icon: <FiEye />,
        tooltip: t('common.view'),
        onClick: () => setSelectedMerchantId(merchant.id),
        size: 'xs',
        variant: 'ghost',
      },
    ]
  }

  // Handle filter changes
  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFilters(newFilters)
    setPage(1)
  }

  // Handle pagination changes
  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (_newSize: number) => {
    // Page size is fixed.
  }

  const handleReviewFiltersChange = (newFilters: Record<string, any>) => {
    setReviewFilters((prev) => ({ ...prev, ...newFilters }))
    setReviewPage(1)
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    if (!persistFilters) {
      removeFunctionalStorage(filterStorageKey)
      return
    }

    const payload = {
      persist: true,
      filters,
    }

    try {
      writeFunctionalStorage(filterStorageKey, JSON.stringify(payload))
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[MerchantsPage] Failed to persist filters', error)
      }
    }
  }, [persistFilters, filters])

  // Handle export
  const handleExport = async () => {
    try {
      const { blob, filename } = await merchantService.exportCsv({
        search: filters.search || '',
        status: filters.status || '',
      }, currentEnv)
      triggerBrowserDownload(blob, filename)
      toast.success({
        title: t('common.exportCsv'),
        description: t('common.export.downloaded'),
      })
    } catch (error) {
      toast.error({
        title: t('common.exportCsv'),
        description:
          error instanceof Error ? error.message : t('common.operationFailed'),
      })
    }
  }

  return (
    <Box>
      <VStack align="stretch" spacing={6}>
        <IdLookupPanel entity="merchant" />
        <Box>
          <HStack justify="space-between" align="start" mb={2}>
            <Box>
              <Heading size="lg" mb={2}>
                {t('merchants.title')}
              </Heading>
              <HStack spacing={4}>
                <Text color={subtitleColor}>
                  {t('merchants.subtitle')}
                </Text>
                {isFetching && (
                  <HStack spacing={2} color="blue.500" aria-live="polite">
                    <InlineSkeleton isCircle width="14px" height="14px" />
                    <Text fontSize="sm">{t('common.processing')}</Text>
                  </HStack>
                )}
              </HStack>
            </Box>
            
            <VStack align="end" spacing={2}>
              <HStack spacing={2}>
                <RefreshIconButton
                  label={t('common.refresh')}
                  tooltipPlacement="left"
                  onClick={handleRefresh}
                  isLoading={isFetching}
                  isDisabled={isFetching}
                />
              </HStack>
              {dataUpdatedAt && (
                <Text fontSize="xs" color={labelColor}>
                  {getLastUpdatedText()}
                </Text>
              )}
            </VStack>
          </HStack>
        </Box>

        {/* DataTable with Merchants */}
        <Box>
          {/* Action Buttons */}
          <Box mb={4}>
            <HStack spacing={3}>
              <Button colorScheme="green">
                {t('merchants.addMerchant')}
              </Button>
            </HStack>
          </Box>

          <DataTable<any>
            data={data?.data || []}
            loading={isLoading}
            error={error}
            columns={columns}
            filters={filterConfigs}
            initialFilters={filters}
            onFiltersChange={handleFiltersChange}
            showFilterBar={true}
            sortable={true}
            pagination={paginationConfig}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            actions={getRowActions}
            actionsColumnWidth="5%"
            size="md"
            emptyMessage={t('merchants.noMerchants')}
            showExport={true}
            onExport={handleExport}
            mobileBreakpoint="lg"
            persistOption={{
              label: t('merchants.rememberFilters'),
              checked: persistFilters,
              onChange: setPersistFilters,
              colorScheme: 'blue',
            }}
          />
        </Box>

        <Box>
          <Heading size="md" mb={4}>
            {t('merchants.reviewList.title')}
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mb={4}>
            <Box p={4} borderWidth="1px" borderRadius="lg">
              <Stat>
                <StatLabel>{t('merchants.reviewList.stats.pendingTotal')}</StatLabel>
                <StatNumber>{pendingReviewSummary?.pagination?.total ?? 0}</StatNumber>
              </Stat>
            </Box>
            <Box p={4} borderWidth="1px" borderRadius="lg">
              <Stat>
                <StatLabel>{t('merchants.reviewList.chainLabels.evm')}</StatLabel>
                <StatNumber>{pendingEvmSummary?.pagination?.total ?? 0}</StatNumber>
              </Stat>
            </Box>
            <Box p={4} borderWidth="1px" borderRadius="lg">
              <Stat>
                <StatLabel>{t('merchants.reviewList.chainLabels.tron')}</StatLabel>
                <StatNumber>{pendingTronSummary?.pagination?.total ?? 0}</StatNumber>
              </Stat>
            </Box>
            <Box p={4} borderWidth="1px" borderRadius="lg">
              <Stat>
                <StatLabel>{t('merchants.reviewList.chainLabels.solana')}</StatLabel>
                <StatNumber>{pendingSolanaSummary?.pagination?.total ?? 0}</StatNumber>
              </Stat>
            </Box>
          </SimpleGrid>

          <DataTable<MerchantChainReviewItem>
            data={pendingReviews?.data || []}
            loading={isReviewLoading}
            error={reviewError}
            columns={reviewColumns}
            filters={reviewFilterConfigs}
            initialFilters={reviewFilters}
            onFiltersChange={handleReviewFiltersChange}
            showFilterBar={true}
            pagination={reviewPaginationConfig}
            onPageChange={setReviewPage}
            onPageSizeChange={() => {}}
          actions={(row) => [
            {
              label: t('common.approve'),
              onClick: () => setChainReviewAction({
                action: 'approve',
                merchantId: row.merchant.id,
                merchantName: row.merchant.name || t('common.unknown'),
                compatibility: row.chainAccess.compatibility,
                reviewRevisionId: row.chainAccess.reviewRevisionId ?? '',
              }),
              size: 'xs',
              colorScheme: 'green',
              variant: 'outline',
              disabled:
                row.chainAccess.approvalStatus !== 'pending_review' ||
                !row.chainAccess.planAllowed,
            },
            {
              label: t('common.reject'),
              onClick: () => setChainReviewAction({
                action: 'reject',
                merchantId: row.merchant.id,
                merchantName: row.merchant.name || t('common.unknown'),
                compatibility: row.chainAccess.compatibility,
                reviewRevisionId: row.chainAccess.reviewRevisionId ?? '',
              }),
              size: 'xs',
              colorScheme: 'orange',
              variant: 'outline',
              disabled: row.chainAccess.approvalStatus !== 'pending_review',
            },
            {
              label: '',
              icon: <FiEye />,
                tooltip: t('common.view'),
                onClick: () => setSelectedMerchantId(row.merchant.id),
                size: 'xs',
                variant: 'ghost',
              },
            ]}
            actionsColumnWidth="5%"
            size="md"
            emptyMessage={t('merchants.reviewList.empty')}
            mobileBreakpoint="lg"
          />
        </Box>
      </VStack>
      
      <MerchantDetailModal
        merchantId={selectedMerchantId}
        isOpen={!!selectedMerchantId}
        onClose={() => setSelectedMerchantId(null)}
      />

      <MerchantChainReviewDialog
        isOpen={!!chainReviewAction}
        action={chainReviewAction?.action ?? null}
        merchantName={chainReviewAction?.merchantName ?? ''}
        chainLabel={chainReviewAction ? getChainLabel(chainReviewAction.compatibility) : ''}
        onClose={() => setChainReviewAction(null)}
        onConfirm={handleChainReviewConfirm}
        isLoading={approveChain.isPending || rejectChain.isPending}
      />
    </Box>
  )
}
